import type { InspectionSessionListApiData } from '@navio/nsdk';
import { filterUnitFor, useFilterSpecValue, useFilterUnits } from 'noddi-ui-web';
import { INSPECTION_CONTEXT_OPTIONS, INSPECTION_PURPOSE_OPTIONS, INSPECTION_SOURCE_OPTIONS } from 'noddi-util';
import { useAccountOptions } from '../Account/useAccountFilter';
import { CREATED_AT_FILTER_TEXT } from '../Date/useCreatedAtFilter';
import { UPDATED_AT_FILTER_TEXT } from '../Date/useUpdatedAtFilter';
import {
  SERVICE_DEPARTMENT_FILTER_TEXT,
  useServiceDepartmentOptions
} from '../ServiceDepartment/useServiceDepartmentFilter';
import {
  SERVICE_ORGANIZATION_FILTER_TEXT,
  useServiceOrganizationOptions
} from '../ServiceOrganization/useServiceOrganizationFilter';

const unit =
  filterUnitFor<
    Omit<NonNullable<InspectionSessionListApiData['query']>, 'pageIndex' | 'pageSize' | 'ordering' | 'search'>
  >();

/**
 * Partner sees one organization's sessions, so its departments scope the list instead of
 * filtering it: the two owner controls are replaced by a locked department set.
 */
export type InspectionSessionTableScope = { serviceDepartmentIds: number[] };

export const useInspectionSessionTableFilters = (scope?: InspectionSessionTableScope) => {
  const organizations = unit.multiSelect('serviceOrganizationIds', {
    ...SERVICE_ORGANIZATION_FILTER_TEXT,
    source: useServiceOrganizationOptions(),
    exclude: true
  });
  const { serviceOrganizationIds } = useFilterSpecValue(organizations);
  const departments = unit.multiSelect('serviceDepartmentIds', {
    ...SERVICE_DEPARTMENT_FILTER_TEXT,
    source: useServiceDepartmentOptions({ serviceOrganizationIds }),
    exclude: true
  });
  return useFilterUnits([
    scope && unit.locked(scope),
    !scope && organizations,
    !scope && departments,
    unit.multiSelect('sources', {
      title: 'Source',
      description: 'Filter by inspection origin',
      options: INSPECTION_SOURCE_OPTIONS,
      exclude: true
    }),
    unit.multiSelect('contexts', {
      title: 'Context',
      description: 'Filter by when the inspection was performed',
      options: INSPECTION_CONTEXT_OPTIONS,
      exclude: true
    }),
    unit.multiSelect('purposes', {
      title: 'Purpose',
      description: 'Filter by inspection purpose',
      options: INSPECTION_PURPOSE_OPTIONS,
      exclude: true
    }),
    unit.multiSelect('createdByUserIds', {
      title: 'Created by',
      description: 'Filter by who created the inspection',
      source: useAccountOptions(),
      exclude: true
    }),
    unit.dateRange('createdAt', { ...CREATED_AT_FILTER_TEXT, exclude: true }),
    unit.dateRange('updatedAt', { ...UPDATED_AT_FILTER_TEXT, exclude: true })
  ]);
};
